// Role Selection Page - Updated to match the image design
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useProfileInfo } from "../contexts/ProfileInfoContext";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../supabase";
import Button from "../components/Button";
import useSnackbarNotification from "../hooks/useSnackbarNotification";

const RoleSelectionPage = () => {
  const { createProfileInfo, profileInfo, loading: profileLoading } = useProfileInfo();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showError } = useSnackbarNotification();
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  // Handle email verification callback and session refresh when page loads
  // This handles the case where user verifies email, leaves tab, and comes back later
  useEffect(() => {
    const handleSessionAndVerification = async () => {
      try {
        // Check for email verification tokens in URL hash (Supabase callback)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const type = hashParams.get('type');
        
        // If we have verification tokens in URL, Supabase will handle them via onAuthStateChange
        // But we should also check current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        // If we have tokens in URL but no session yet, wait a moment for onAuthStateChange to process
        if (accessToken && type === 'signup' && !session) {
          console.log('[RoleSelectionPage] Email verification tokens detected, waiting for auth state change...');
          // Give Supabase a moment to process the verification
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Check again after waiting
          const { data: { session: newSession } } = await supabase.auth.getSession();
          if (!newSession) {
            console.warn('[RoleSelectionPage] Session not created from verification tokens');
          }
        }
        
        // If no session and no verification tokens, session may have expired
        if (!session && !accessToken) {
          console.warn('[RoleSelectionPage] No active session found');
          // Don't redirect immediately - let the user see the page
          // They can try to log in if needed
        }
      } catch (error) {
        console.error('[RoleSelectionPage] Error handling session:', error);
      } finally {
        setCheckingSession(false);
      }
    };

    handleSessionAndVerification();
  }, []);

  // Check if user already has a profile with a role selected
  // CRITICAL: Always check users_profiles table, not cache
  useEffect(() => {
    if (!user || profileLoading || checkingSession) return;

    // Force fetch from database to ensure we have the correct role
    const checkProfileFromDatabase = async () => {
      try {
        const { data: dbProfile } = await supabase
          .from("users_profiles")
          .select("role")
          .eq("profileid", user.id)
          .single();

        // If profile exists in database with a role, redirect to appropriate dashboard
        if (dbProfile && dbProfile.role) {
          console.log(`[RoleSelectionPage] Profile found in database with role: ${dbProfile.role} - Redirecting`);
          const dashboardPath = dbProfile.role === 'Employer' 
            ? "/employer/dashboard" 
            : "/employee/dashboard";
          navigate(dashboardPath, { replace: true });
        }
      } catch (error) {
        // Profile doesn't exist or error - that's okay, user can select role
        console.log('[RoleSelectionPage] No profile in database or error:', error);
      }
    };

    checkProfileFromDatabase();
  }, [user, profileLoading, navigate]);

  const handleRoleSelection = async (role) => {
    setLoading(true);
    try {
      // CRITICAL: Verify user is authenticated and session is valid
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (!session || !user) {
        showError("Your session has expired. Please log in to continue.");
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 2000);
        setLoading(false);
        return;
      }

      // Verify email is confirmed
      if (!session.user.email_confirmed_at) {
        showError("Please verify your email address before creating a profile.");
        navigate("/email-verification", { 
          state: { email: session.user.email } 
        });
        setLoading(false);
        return;
      }

      // CRITICAL: Double-check database before allowing role selection
      // This prevents role switching if user somehow bypassed the redirect
      const { data: existingProfile, error: profileError } = await supabase
        .from("users_profiles")
        .select("role")
        .eq("profileid", user.id)
        .single();

      // Handle errors properly - only PGRST116 (not found) means no profile exists
      if (profileError) {
        if (profileError.code === 'PGRST116') {
          // Profile doesn't exist - this is fine, proceed with role creation
          console.log('[RoleSelectionPage] No existing profile found (PGRST116), proceeding with role selection');
        } else {
          // Any other error (network, timeout, permission, etc.) - fail safely
          console.error(`[RoleSelectionPage] Error checking existing profile:`, profileError);
          showError(`Failed to verify existing profile. Cannot proceed with role selection. Please try again or contact support.`);
          setLoading(false);
          return;
        }
      }

      // If profile exists with a role, handle accordingly
      if (existingProfile?.role) {
        // If trying to switch roles, prevent it
        if (existingProfile.role !== role) {
          const errorMsg = `You already have a ${existingProfile.role} account. Cannot switch to ${role}.`;
          console.error(`[RoleSelectionPage] BLOCKED: ${errorMsg}`);
          showError(errorMsg);
          // Redirect to appropriate dashboard
          const dashboardPath = existingProfile.role === 'Employer' 
            ? "/employer/dashboard" 
            : "/employee/dashboard";
          navigate(dashboardPath, { replace: true });
          setLoading(false);
          return;
        }
        
        // Profile exists with the same role - user shouldn't be here, redirect to dashboard
        console.log(`[RoleSelectionPage] Profile already exists with role: ${existingProfile.role} - Redirecting to dashboard`);
        const dashboardPath = existingProfile.role === 'Employer' 
          ? "/employer/dashboard" 
          : "/employee/dashboard";
        navigate(dashboardPath, { replace: true });
        setLoading(false);
        return;
      }

      // Only create profile if it doesn't exist (profileError.code === 'PGRST116')
      // Check if user has existing metadata (existing user)
      const hasExistingData = user?.user_metadata && Object.keys(user.user_metadata).length > 0;
      
      await createProfileInfo({ 
        role,
        onboardingStep: 0,
        onboardingCompleted: false,
        firstTimeLogin: !hasExistingData  // True only for truly new users
      });
      
      // Route based on role naming (Employer -> company setup page, Employee -> personal profile)
      if (role === 'Employer') {
        navigate("/employer-profile", { replace: true });
      } else {
        navigate("/employee-profile", { replace: true });
      }
    } catch (error) {
      console.error("Error creating profile:", error);
      // Check if error is about role change
      if (error.message?.includes("Cannot change role")) {
        showError(error.message);
        // Redirect to appropriate dashboard based on existing role
        const { data: existingProfile } = await supabase
          .from("users_profiles")
          .select("role")
          .eq("profileid", user.id)
          .single();
        if (existingProfile?.role) {
          const dashboardPath = existingProfile.role === 'Employer' 
            ? "/employer/dashboard" 
            : "/employee/dashboard";
          navigate(dashboardPath, { replace: true });
        }
      } else {
        showError("Failed to create profile. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExistingUser = () => {
    // Navigate to the main dashboard/home page for existing users
    navigate("/", { replace: true });
  };

  // Show loading while checking session or profile
  if (checkingSession || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  // If no user after session check, show message and redirect
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Session Expired</h2>
          <p className="text-gray-600 mb-6">Please log in to continue creating your profile.</p>
          <Button
            onClick={() => navigate("/login", { replace: true })}
            className="bg-black text-white px-6 py-2 rounded-full font-medium hover:bg-gray-800"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  // Don't render if user already has a profile with a role (redirect is happening)
  if (profileInfo && profileInfo.role) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center py-10 px-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full">
        {/* Header with BETA tag */}
        <div className="flex items-center justify-center mb-6">
          <h1 className="text-3xl font-bold text-black mr-3">Doctors' Jobs</h1>
          <span className="bg-gray-200 text-black text-xs px-2 py-1 rounded">BETA</span>
        </div>

        {/* Welcome message */}
        <div className="text-left mb-8">
          <h2 className="text-2xl font-bold text-black mb-4">Welcome to Doctors' Jobs!</h2>
          <p className="text-gray-600 text-sm">
            Track opportunities, sharpen your resume, and take control of your next career move, with support that's designed just for doctors.
          </p>
        </div>

        {/* Role selection cards - only two cards */}
        <div className="flex gap-3 mb-8">
          {/* Get a Job Card */}
          <div 
            className={`flex-1 rounded-xl py-16 px-4 cursor-pointer transition-all duration-200 flex flex-col items-center justify-center gap-4 ${
              selectedRole === 'Employee' 
                ? 'bg-blue-100 border-2 border-blue-300' 
                : 'bg-white border border-gray-300 hover:border-gray-400'
            }`}
            onClick={() => setSelectedRole('Employee')}
          >
            <img 
              src="/Find a new role.svg" 
              alt="Find a new role" 
              className="w-10 h-10"
            />
            <h3 className="text-lg font-semibold text-black text-center">Find a new role</h3>
          </div>

          {/* Hire Talent Card */}
          <div 
            className={`flex-1 rounded-xl py-16 px-4 cursor-pointer transition-all duration-200 flex flex-col items-center justify-center gap-4 ${
              selectedRole === 'Employer' 
                ? 'bg-blue-100 border-2 border-blue-300' 
                : 'bg-white border border-gray-300 hover:border-gray-400'
            }`}
            onClick={() => setSelectedRole('Employer')}
          >
            <img 
              src="/Hire Talent.svg" 
              alt="Hire Talent" 
              className="w-10 h-10"
            />
            <h3 className="text-lg font-semibold text-black text-center">Hire Talent</h3>
          </div>
        </div>

        {/* Get started button - centered */}
        <div className="flex flex-col items-center gap-4">
          {selectedRole && (
            <Button
              onClick={() => handleRoleSelection(selectedRole)}
              disabled={loading}
              className="bg-black text-white px-8 py-3 rounded-full font-medium hover:bg-gray-800 transition-colors"
            >
              {loading ? "Creating Profile..." : "Get started"}
            </Button>
          )}
          {/* <button
            onClick={handleExistingUser}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Already have an account? <span className="underline">Log In</span>
          </button> */}
        </div>
      </div>
    </div>
  );
};

export default RoleSelectionPage;
